import { useState } from 'react';
import { AxiosError } from 'axios';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useAuthStore } from '@/stores/auth';
import { useUpdateClient, type ClientRecord } from '@/api/clients';
import { WeeklyAvailabilityEditor } from '@/components/ui/WeeklyAvailabilityEditor';
import type { Availability } from '@/lib/availability';

/*
 * Client Availability tab — weekly pattern the scheduler uses to warn
 * (never block) when a session is booked outside the client's window.
 * Mirrors the shape stored on staff records. Edits PATCH the client
 * record; no new endpoint.
 *
 * The editor shows live values inline; the Save button commits the
 * pending state. Cancel reverts to whatever the server has.
 */
export function ClientAvailabilityTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_clients'));
    const updateMut = useUpdateClient(c.id);

    const [draft, setDraft] = useState<Availability>(c.availability ?? {});
    const [editing, setEditing] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const saved = c.availability ?? {};
    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

    function onStartEdit() {
        setDraft(c.availability ?? {});
        setErr(null);
        setEditing(true);
    }

    function onCancel() {
        setDraft(c.availability ?? {});
        setErr(null);
        setEditing(false);
    }

    async function onSave() {
        setErr(null);
        try {
            await updateMut.mutateAsync({ availability: draft });
            setEditing(false);
        } catch (e) {
            if (e instanceof AxiosError) {
                setErr(e.response?.data?.error ?? 'Failed to save availability');
            } else {
                setErr('Unexpected error');
            }
        }
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between gap-3 w-full">
                    <div>
                        <CardTitle>Availability</CardTitle>
                        <p className="text-xs text-text-subtle mt-1">
                            Days and times this client is available for sessions. The
                            scheduler warns (but never blocks) when a session falls
                            outside this window.
                        </p>
                    </div>
                    {canEdit && !editing && (
                        <Button variant="ghost" size="sm" onClick={onStartEdit}>
                            Edit
                        </Button>
                    )}
                </div>
            </CardHeader>

            {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

            <WeeklyAvailabilityEditor
                value={editing ? draft : saved}
                onChange={setDraft}
                disabled={!editing}
            />

            {editing && (
                <div className="mt-4 flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={onCancel}
                        disabled={updateMut.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        type="button"
                        onClick={onSave}
                        disabled={!dirty || updateMut.isPending}
                    >
                        {updateMut.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            )}
        </Card>
    );
}
